
import React, { useState, useEffect } from 'react';
import OnboardingLayout from '../../OnboardingLayout';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, AlertCircle, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SUBSCRIPTION_PLANS, loadStripe } from '@/services/stripeService';

interface SubscriptionOptionsProps {
  formData: any;
  updateFormData: (data: any) => void;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const SubscriptionOptionsProps: React.FC<SubscriptionOptionsProps> = ({
  formData,
  updateFormData,
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  const [selectedPlan, setSelectedPlan] = useState(formData.subscription || 'basic');
  const [stripeReady, setStripeReady] = useState(false);
  
  useEffect(() => {
    const initStripe = async () => {
      await loadStripe();
      setStripeReady(true);
    };
    
    initStripe();
  }, []);
  
  const handlePlanSelect = (plan: string) => {
    setSelectedPlan(plan);
    updateFormData({ subscription: plan });
  };
  
  const handleNext = () => {
    updateFormData({ subscription: selectedPlan });
    onNext();
  };
  
  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      description: 'Free reading with limited access',
      price: 'Free',
      features: [
        'Access to free articles',
        'Follow up to 3 journalists',
        'Join discussions with comments',
        'Receive newsletter updates',
      ],
      limitations: [
        'Limited access to premium content',
        'No AI-powered features',
        'No audio versions of articles'
      ],
      recommended: false
    },
    {
      id: 'premium',
      name: 'Premium',
      description: 'Full platform access with enhanced features',
      price: '$9.99/month',
      features: [
        'Full access to all platform features',
        'Follow unlimited journalists',
        'Engage with premium content',
        'AI summaries and audio versions',
        'Priority comment visibility',
        'Ad-free reading experience',
        'Export articles to read offline'
      ],
      limitations: [],
      recommended: true
    },
    {
      id: 'custom',
      name: 'Individual Subscriptions',
      description: 'Subscribe directly to specific journalists',
      price: 'Varies',
      features: [
        'Pay only for what you read',
        'Support journalists directly',
        'Access subscriber-only content',
        'Join exclusive communities',
        'Individual journalist perks'
      ],
      limitations: [
        'Features vary by journalist',
        'No platform-wide benefits'
      ],
      recommended: false
    }
  ];

  return (
    <OnboardingLayout
      title="Choose Your Subscription"
      subtitle="Select the plan that best fits your reading habits"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={handleNext}
      onPrevious={onPrevious}
    >
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {plans.map((plan) => (
            <Card 
              key={plan.id} 
              className={`relative overflow-hidden ${
                selectedPlan === plan.id 
                  ? 'border-primary ring-1 ring-primary' 
                  : ''
              }`}
            >
              {plan.recommended && (
                <div className="absolute top-0 right-0">
                  <Badge className="rounded-tl-none rounded-br-none bg-primary text-primary-foreground">
                    <Star className="h-3 w-3 mr-1" /> Recommended
                  </Badge>
                </div>
              )}
              
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                <p className="text-muted-foreground text-sm mb-4">{plan.description}</p>
                
                <div className="mb-6">
                  <span className="text-2xl font-bold">{plan.price}</span>
                  {plan.id !== 'basic' && plan.id !== 'custom' && (
                    <span className="text-muted-foreground text-sm ml-1">
                      or $99/year
                    </span>
                  )}
                </div>
                
                <div className="space-y-3">
                  <h4 className="text-sm font-medium">Includes:</h4>
                  <ul className="space-y-2">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-sm">
                        <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {plan.limitations.length > 0 && (
                    <>
                      <h4 className="text-sm font-medium pt-2">Limitations:</h4>
                      <ul className="space-y-2">
                        {plan.limitations.map((limitation, index) => (
                          <li key={index} className="flex items-start text-sm text-muted-foreground">
                            <span className="mr-2">•</span>
                            <span>{limitation}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </CardContent>
              
              <CardFooter className="pt-2 pb-6">
                <Button 
                  variant={selectedPlan === plan.id ? "default" : "outline"}
                  className="w-full"
                  onClick={() => handlePlanSelect(plan.id)}
                >
                  {selectedPlan === plan.id ? "Selected" : "Select Plan"}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
        
        <div className="flex items-start gap-3 bg-muted/30 rounded-lg p-4">
          <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium mb-1">Flexible options</h4>
            <p className="text-sm text-muted-foreground">
              You can always change your subscription later. Your selected plan will take effect after 
              completing the onboarding process. Individual journalist subscriptions can be added 
              separately from each journalist's profile.
            </p>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default SubscriptionOptionsProps;
